
import { CreateElement, VNode } from 'vue';
import { Component, Prop, Vue, Watch, Emit } from 'vue-property-decorator';
import { timedAsync } from '../utils/timed-async';

import axios, { AxiosResponse } from 'axios';

type Status = 'initial' | 'loading' | 'success' | 'error';
const ST_INITIAL = 'initial';
const ST_LOADING = 'loading';
const ST_SUCCESS = 'success';
const ST_FAILURE = 'error';

@Component
export default class Rest extends Vue {
  public currentTick!: number;
  public status!: Status;
  public result: Record<string, any> = {};
  @Prop() protected endpoint!: string;
  @Prop({default: 'get'}) protected method!: 'post' | 'put' | 'get' | 'delete';
  @Prop({default: true}) protected autoload!: boolean;
  @Prop({default: false}) protected loadOnInput!: boolean;
  @Prop() protected tick!: number;
  @Prop({default: () => ({})}) protected input!: Record<string, any>;
  @Prop({default: false}) protected timed!: boolean;
  private inputDebounce!: number;

  protected data() {
    return {
      currentTick: this.currentTick,
      status: this.status,
      result: this.result,
    };
  }

  @Watch('tick')
  protected tickUpdated() {
    this.sendRequest();
  }

  protected updateTick() {
    this.currentTick = new Date().getTime();
  }

  protected send() {
    this.sendRequest();
  }

  protected beforeMount(): void {
    this.status = ST_INITIAL;
    if (this.method === 'get' && this.autoload && !this.loadOnInput || this.loadOnInput && this.input) {
      this.sendRequest();
    }
  }

  @Watch('input', { deep: true })
  protected inputUpdated() {
    if (this.loadOnInput && this.input) {
      // Debounce
      if (this.inputDebounce) {
        window.clearTimeout(this.inputDebounce);
      }
      this.inputDebounce = window.setTimeout(() => {
        this.sendRequest();
      }, 250);
    }
  }

  @Watch('endpoint')
  protected endpointUpdated() {
    this.sendRequest();
  }

  protected async sendRequest(): Promise<void> {
    this.status = ST_LOADING;
    const method = this.method;
    let promise: Promise<AxiosResponse>;
    const endpoint = await this.endpoint;
    if (!endpoint) {
      // tslint:disable-next-line: no-console
      console.log('[REST] Cancelled request without endpoint', method, JSON.parse(JSON.stringify(this.input)));
      this.status = ST_INITIAL;
      return;
    }
    if ('post' === method || 'put' === method) {
      promise = axios[method](endpoint, this.input);
    } else if ('get' === method) {
      promise = axios.get(endpoint, { params: this.input });
    } else if ('delete' === method) {
      promise = axios.delete(endpoint);
    } else {
      throw new Error(`Unknown API method: ${this.method}`);
    }
    // tslint:disable-next-line: no-console
    console.log('[REST] Request', method, endpoint, JSON.parse(JSON.stringify(this.input)));
    try {
      const response = await timedAsync<AxiosResponse>(promise, { fastTime: this.timed ? 500 : 1 });
      if (response.status >= 200 && response.status <= 299) {
        this.status = ST_SUCCESS;
        if (response.data) {
          // tslint:disable-next-line: no-console
          console.log('[REST] Loaded data', response.data);
          this.result = response.data;
          this.loaded(this.result);
        }
      } else {
        this.status = ST_FAILURE;
        this.error(response.data);
      }
    } catch(e) {
      // tslint:disable-next-line: no-console
      console.error(e);
      this.status = ST_FAILURE;
      this.error(e.response.data);
    }
  }

  @Emit()
  protected loaded(result: Record<string, any>) {
    return result;
  }

  @Emit()
  protected error(errorData: any): any {
    // tslint:disable-next-line: no-console
    console.log('[REST] Error', errorData);
    if (typeof (this as any).$toasts !== 'undefined') {
      const text = errorData.message ? errorData.message : errorData.toString();
      if (text) {
        (this as any).$toasts.add({
          type: 'danger',
          text,
        });
      }
    }
    return errorData;
  }

  protected render(h: CreateElement): VNode {
    if (this.$scopedSlots) {
      return this.$scopedSlots.default!({
        status: this.status,
        result: this.result,
        send: this.send,
      }) as any;
    }
    return h();
  }
}
